import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

// Prefer CRA-style env if it exists at build time; otherwise choose by hostname (sandbox vs main).
const API_BASE =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL && String(process.env.REACT_APP_BACKEND_URL)) ||
  ((typeof window !== 'undefined' && String(window.location.hostname).indexOf('ai-translation-frontend-sandbox') !== -1)
    ? 'https://ai-translation-backend-sandbox.onrender.com'
    : 'https://ai-translation-backend.onrender.com');

export default function PurchaseHistory() {
  const { jwt } = useAuth();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [days, setDays] = useState(30);
  const [provider, setProvider] = useState("");
  const [status, setStatus] = useState("");
  const [email, setEmail] = useState("");
  const [limit, setLimit] = useState(200);

  const headers = jwt ? { Authorization: `Bearer ${jwt}` } : {};

  const providerLabel = (p) => {
    const v = String(p || '').toLowerCase();
    if (v === 'stripe_project') return 'Custom Translation (Stripe)';
    if (v === 'paypal_project') return 'Custom Translation (PayPal)';
    if (v === 'stripe') return 'Credits (Stripe)';
    if (v === 'paypal') return 'Credits (PayPal)';
    return String(p || '');
  };


  const formatStatusLabel = (s) => {
    try {
      const v = String(s || "");
      if (!v) return "";
      return v
        .split("_")
        .map((p) => (p ? p.charAt(0).toUpperCase() + p.slice(1) : ""))
        .join(" ");
    } catch {
      return String(s || "");
    }
  };

  const downloadCsv = async () => {
    setMsg("");
    try {
      const res = await axios.get(`${API_BASE}/admin/payments/history.csv`, {
        headers,
        params: {
          days,
          provider: provider || undefined,
          status: status || undefined,
          email: email || undefined,
          limit,
        },
        responseType: "blob",
      });

      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "purchase_history.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Failed to download CSV.");
    }
  };

  const fetchHistory = async () => {
    setLoading(true);
    setMsg("");
    try {
      const res = await axios.get(`${API_BASE}/admin/payments/history`, {
        headers,
        params: {
          days,
          provider: provider || undefined,
          status: status || undefined,
          email: email || undefined,
          limit,
        },
      });
      setRows(res.data || []);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Failed to load purchase history.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line
  }, []);


  const getRowStyle = (row) => {
    try {
      const s = String(row?.status || "").toLowerCase();
      if (s === "succeeded") {
        // Brand green (semi-transparent)
        return { backgroundColor: "rgba(148, 200, 61, 0.18)" };
      }
      if (s.startsWith("failed")) {
        // Semi-transparent red for failures
        return { backgroundColor: "rgba(220, 38, 38, 0.14)" };
      }
      // "created" and any other status: default background
      return undefined;
    } catch {
      return undefined;
    }
  };

  return (
    <div data-testid="purchase-history-page" className="space-y-4">
      <h3 data-testid="purchase-history-title" className="font-semibold">Purchase History</h3>

      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-sm font-medium">Days</label>
          <input
            type="number"
            min="0"
            step="1"
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value || "0", 10))}
            className="border rounded p-2 w-28"
          />
        </div>
        <div>
          <label className="block text-sm font-medium">Provider</label>
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            className="border rounded p-2 w-40"
          >
            <option value="">All</option>
            <option value="stripe">Credits (Stripe)</option>
            <option value="paypal">Credits (PayPal)</option>
            <option value="stripe_project">Custom Translation (Stripe)</option>
            <option value="paypal_project">Custom Translation (PayPal)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border rounded p-2 w-48"
          >
            <option value="">All</option>
            <option value="created">Created</option>
            <option value="succeeded">Succeeded</option>
            <option value="failed">Failed</option>
            <option value="failed_mismatch">Failed Mismatch</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium">User Email</label>
          <input
            type="text"
            placeholder="filter by email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="border rounded p-2 w-64"
          />
        </div>
        <div>
          <label className="block text-sm font-medium">Limit</label>
          <input
            type="number"
            min="1"
            step="1"
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value || "1", 10))}
            className="border rounded p-2 w-28"
          />
        </div>
        <button
          data-testid="purchase-history-refresh"
          onClick={fetchHistory}
          disabled={loading}
          className={`text-white px-4 py-2 rounded ${loading ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"}`}
          style={{ backgroundColor: "#00a7e1" }}
        >
          {loading ? "Loading..." : "Refresh"}
        </button>
        <button
          data-testid="purchase-history-download"
          onClick={downloadCsv}
          disabled={loading || rows.length === 0}
          className={`text-white px-4 py-2 rounded ${loading || rows.length === 0 ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"}`}
          style={{ backgroundColor: "#94c83d" }}
        >
          Download CSV
        </button>
      </div>

      {msg && <div className="text-sm">{msg}</div>}

      <div className="overflow-x-auto">
        <table data-testid="purchase-history-table" className="min-w-full border border-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left p-2 border-b">Created</th>
              <th className="text-left p-2 border-b">Provider</th>
              <th className="text-left p-2 border-b">External ID</th>
              <th className="text-left p-2 border-b">User</th>
              <th className="text-right p-2 border-b">USD</th>
              <th className="text-right p-2 border-b">Credits</th>
              <th className="text-left p-2 border-b">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  data-testid="purchase-history-empty"
                  colSpan="7"
                  className="p-4 text-center text-gray-500"
                >
                  {loading ? "Loading..." : "No transactions found."}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={`${row.provider}:${row.external_id}:${row.id}`} className="border-b" style={getRowStyle(row)}>
                  <td className="p-2">{new Date(row.created_at).toLocaleString()}</td>
                  <td className="p-2">{providerLabel(row.provider)}</td>
                  <td className="p-2 font-mono text-xs break-all">{row.external_id}</td>
                  <td className="p-2">{row.user_email || row.user_id}</td>
                  <td className="p-2 text-right">${(row.usd ?? 0).toFixed(2)}</td>
                  <td className="p-2 text-right">{(row.credits ?? 0).toFixed(2)}</td>
                  <td className="p-2">{formatStatusLabel(row.status)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
